import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import TenantForm from './TenantForm';

const STEPS = ['Company', 'Contacts', 'Associate contacts', 'Tenant information'];

const emptyCompany = {
  account_name: '',
  company_name: '',
  company_address: '',
  website: '',
  office_direct_number: '',
  notes: ''
};

const emptyContact = {
  first_name: '',
  last_name: '',
  email: '',
  cell_number: '',
  office_direct_number: '',
  role_title: ''
};

const TenantOnboardWizard = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [companyMode, setCompanyMode] = useState('new'); // new | existing
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [companyForm, setCompanyForm] = useState(emptyCompany);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [account, setAccount] = useState(null);

  const [contacts, setContacts] = useState([]);
  const [contactDraft, setContactDraft] = useState(emptyContact);
  const [addingContact, setAddingContact] = useState(false);

  const [primaryContactId, setPrimaryContactId] = useState('');
  const [selectedContactIds, setSelectedContactIds] = useState([]);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    setAccountsLoading(true);
    try {
      const response = await api.get('/accounts', { params: { page: 1, limit: 500 } });
      setAccounts(response.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load companies');
    } finally {
      setAccountsLoading(false);
    }
  };

  const fetchContacts = async (accountId) => {
    const response = await api.get('/contacts', {
      params: { account_id: accountId, page: 1, limit: 500 }
    });
    const list = response.data.data || [];
    setContacts(list);
    if (list.length > 0) {
      const preferred = list.find((c) => c.is_primary) || list[0];
      setPrimaryContactId((prev) => prev || preferred.id);
      setSelectedContactIds((prev) => (prev.length ? prev : list.map((c) => c.id)));
    }
    return list;
  };

  const validateCompanyStep = () => {
    if (companyMode === 'existing') {
      if (!selectedAccountId) {
        setError('Select an existing company to continue.');
        return false;
      }
      return true;
    }
    if (!companyForm.company_name.trim() && !companyForm.account_name.trim()) {
      setError('Company name is required.');
      return false;
    }
    return true;
  };

  const saveCompanyAndContinue = async () => {
    setError('');
    if (!validateCompanyStep()) return;

    setSaving(true);
    try {
      let currentAccount = account;
      if (companyMode === 'existing') {
        const response = await api.get(`/accounts/${selectedAccountId}`);
        currentAccount = response.data.data;
      } else if (!currentAccount) {
        const name = companyForm.company_name.trim() || companyForm.account_name.trim();
        const payload = {
          ...companyForm,
          account_name: companyForm.account_name.trim() || name,
          company_name: companyForm.company_name.trim() || name
        };
        const response = await api.post('/accounts', payload);
        currentAccount = response.data.data;
        toast.success('Company created');
        await fetchAccounts();
      }
      setAccount(currentAccount);
      setSelectedAccountId(currentAccount.id);
      await fetchContacts(currentAccount.id);
      setActiveStep(1);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save company');
    } finally {
      setSaving(false);
    }
  };

  const addContact = async () => {
    setError('');
    if (!account?.id) {
      setError('Create or select a company first.');
      return;
    }
    if (!contactDraft.first_name.trim() || !contactDraft.email.trim()) {
      setError('Contact first name and email are required.');
      return;
    }
    setAddingContact(true);
    try {
      const response = await api.post('/contacts', {
        ...contactDraft,
        account_id: account.id
      });
      const created = response.data.data?.contact || response.data.data;
      toast.success('Contact added');
      setContactDraft(emptyContact);
      const list = await fetchContacts(account.id);
      const newId = created?.id || list[list.length - 1]?.id;
      if (newId) {
        setSelectedContactIds((prev) => Array.from(new Set([...prev, newId])));
        if (!primaryContactId) setPrimaryContactId(newId);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add contact');
    } finally {
      setAddingContact(false);
    }
  };

  const validateContactsStep = () => {
    if (contacts.length === 0) {
      setError('Add at least one contact before continuing.');
      return false;
    }
    return true;
  };

  const validateAssociateStep = () => {
    if (!primaryContactId) {
      setError('Select a primary contact to associate with the tenant.');
      return false;
    }
    if (!selectedContactIds.includes(primaryContactId)) {
      setError('Primary contact must be included in associated contacts.');
      return false;
    }
    return true;
  };

  const goNext = async () => {
    setError('');
    if (activeStep === 0) {
      await saveCompanyAndContinue();
      return;
    }
    if (activeStep === 1) {
      if (!validateContactsStep()) return;
      setActiveStep(2);
      return;
    }
    if (activeStep === 2) {
      if (!validateAssociateStep()) return;
      setActiveStep(3);
    }
  };

  const goBack = () => {
    setError('');
    setActiveStep((s) => Math.max(0, s - 1));
  };

  const goToStep = async (targetStep) => {
    if (targetStep === activeStep || saving || targetStep < 0 || targetStep >= STEPS.length) {
      return;
    }
    setError('');

    // Leaving company step toward later steps: persist company when possible
    // so contacts/tenant steps have an account to work with.
    if (activeStep === 0 && targetStep > 0 && !account?.id) {
      if (!validateCompanyStep()) {
        // Still allow jump so user can browse, but keep the validation message.
        setActiveStep(targetStep);
        return;
      }
      setSaving(true);
      try {
        let currentAccount = account;
        if (companyMode === 'existing') {
          const response = await api.get(`/accounts/${selectedAccountId}`);
          currentAccount = response.data.data;
        } else {
          const name = companyForm.company_name.trim() || companyForm.account_name.trim();
          const payload = {
            ...companyForm,
            account_name: companyForm.account_name.trim() || name,
            company_name: companyForm.company_name.trim() || name
          };
          const response = await api.post('/accounts', payload);
          currentAccount = response.data.data;
          toast.success('Company created');
          await fetchAccounts();
        }
        setAccount(currentAccount);
        setSelectedAccountId(currentAccount.id);
        await fetchContacts(currentAccount.id);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to save company');
        setSaving(false);
        return;
      } finally {
        setSaving(false);
      }
    }

    if (targetStep === 3 && !primaryContactId) {
      setError('Select a primary contact before opening Tenant information.');
    }
    setActiveStep(targetStep);
  };

  const toggleContactAssociation = (contactId) => {
    setSelectedContactIds((prev) => {
      if (prev.includes(contactId)) {
        if (contactId === primaryContactId) return prev;
        return prev.filter((id) => id !== contactId);
      }
      return [...prev, contactId];
    });
  };

  const selectedAccount = accounts.find((a) => a.id === selectedAccountId);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/tenants')}>
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Onboard Tenant
        </Typography>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel nonLinear sx={{ mb: 4 }}>
          {STEPS.map((label, index) => (
            <Step key={label} completed={index < activeStep}>
              <StepLabel
                onClick={() => goToStep(index)}
                sx={{
                  cursor: saving ? 'default' : 'pointer',
                  '& .MuiStepLabel-label': {
                    cursor: saving ? 'default' : 'pointer',
                    '&:hover': saving ? undefined : { color: 'primary.main' }
                  },
                  '& .MuiStepIcon-root': {
                    cursor: saving ? 'default' : 'pointer'
                  }
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          Click any step above to jump there and fill details in any order.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Company</Typography>
            <RadioGroup
              row
              value={companyMode}
              onChange={(e) => {
                setCompanyMode(e.target.value);
                setAccount(null);
              }}
              sx={{ mb: 2 }}
            >
              <FormControlLabel value="new" control={<Radio />} label="Create new company" />
              <FormControlLabel value="existing" control={<Radio />} label="Use existing company" />
            </RadioGroup>

            {companyMode === 'existing' ? (
              <FormControl fullWidth required>
                <InputLabel>Company (Account)</InputLabel>
                <Select
                  label="Company (Account)"
                  value={selectedAccountId}
                  disabled={accountsLoading}
                  onChange={(e) => setSelectedAccountId(e.target.value)}
                >
                  <MenuItem value="" disabled>Select company</MenuItem>
                  {accounts.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {(a.company_name || a.account_name)}{a.account_code ? ` (${a.account_code})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Company Name"
                    value={companyForm.company_name}
                    onChange={(e) => setCompanyForm({ ...companyForm, company_name: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Account Display Name"
                    value={companyForm.account_name}
                    onChange={(e) => setCompanyForm({ ...companyForm, account_name: e.target.value })}
                    helperText="Optional — defaults to company name"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Company Address"
                    value={companyForm.company_address}
                    onChange={(e) => setCompanyForm({ ...companyForm, company_address: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Website"
                    value={companyForm.website}
                    onChange={(e) => setCompanyForm({ ...companyForm, website: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Office Direct Number"
                    value={companyForm.office_direct_number}
                    onChange={(e) => setCompanyForm({ ...companyForm, office_direct_number: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Notes"
                    value={companyForm.notes}
                    onChange={(e) => setCompanyForm({ ...companyForm, notes: e.target.value })}
                  />
                </Grid>
              </Grid>
            )}

            {companyMode === 'existing' && selectedAccount && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Selected: <strong>{selectedAccount.company_name || selectedAccount.account_name}</strong>
              </Alert>
            )}
          </Box>
        )}

        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Contacts for {account?.company_name || account?.account_name || 'company'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add one or more people at this company. You will associate them on the next step.
            </Typography>

            {contacts.length > 0 && (
              <Box sx={{ mb: 3 }}>
                {contacts.map((c) => (
                  <Paper key={c.id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                    <Typography fontWeight={600}>
                      {c.first_name} {c.last_name || ''}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {c.email}{c.cell_number ? ` · ${c.cell_number}` : ''}{c.role_title ? ` · ${c.role_title}` : ''}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ mb: 2 }}>Add contact</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="First Name"
                  value={contactDraft.first_name}
                  onChange={(e) => setContactDraft({ ...contactDraft, first_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={contactDraft.last_name}
                  onChange={(e) => setContactDraft({ ...contactDraft, last_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  type="email"
                  label="Email"
                  value={contactDraft.email}
                  onChange={(e) => setContactDraft({ ...contactDraft, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Role Title"
                  value={contactDraft.role_title}
                  onChange={(e) => setContactDraft({ ...contactDraft, role_title: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Cell Number"
                  value={contactDraft.cell_number}
                  onChange={(e) => setContactDraft({ ...contactDraft, cell_number: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Office Direct Number"
                  value={contactDraft.office_direct_number}
                  onChange={(e) => setContactDraft({ ...contactDraft, office_direct_number: e.target.value })}
                />
              </Grid>
            </Grid>
            <Button
              sx={{ mt: 2 }}
              variant="outlined"
              disabled={addingContact}
              onClick={addContact}
              startIcon={addingContact ? <CircularProgress size={16} /> : null}
            >
              {addingContact ? 'Adding…' : 'Add this contact'}
            </Button>
          </Box>
        )}

        {activeStep === 2 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 1 }}>Associate contacts</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose which contacts belong to this tenant and pick the primary contact.
            </Typography>
            <RadioGroup
              value={primaryContactId}
              onChange={(e) => {
                const id = e.target.value;
                setPrimaryContactId(id);
                setSelectedContactIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
              }}
            >
              {contacts.map((c) => (
                <Paper key={c.id} variant="outlined" sx={{ p: 1.5, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox
                    checked={selectedContactIds.includes(c.id)}
                    onChange={() => toggleContactAssociation(c.id)}
                    disabled={c.id === primaryContactId}
                  />
                  <FormControlLabel
                    value={c.id}
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography fontWeight={600}>
                          {c.first_name} {c.last_name || ''}
                          {c.id === primaryContactId ? ' (primary)' : ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">{c.email}</Typography>
                      </Box>
                    }
                    sx={{ m: 0, flex: 1 }}
                  />
                </Paper>
              ))}
            </RadioGroup>
          </Box>
        )}

        {activeStep === 3 && account?.id && primaryContactId && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Same form as Quick Add Tenant. Company and primary contact are locked from earlier steps.
              {' '}
              <strong>{account?.company_name || account?.account_name}</strong>
              {' · '}
              <strong>{contacts.find((c) => c.id === primaryContactId)?.email || '—'}</strong>
            </Alert>
            <TenantForm
              embedded
              lockAccountId={account.id}
              lockContactId={primaryContactId}
              onCancel={() => setActiveStep(2)}
              onSuccess={(created) => navigate(created?.id ? `/tenants/${created.id}` : '/tenants')}
            />
          </Box>
        )}

        {activeStep === 3 && (!account?.id || !primaryContactId) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Complete Company and Associate contacts first, then open Tenant information.
          </Alert>
        )}

        {activeStep < STEPS.length - 1 && (
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
            <Button disabled={activeStep === 0 || saving} onClick={goBack}>
              Back
            </Button>
            <Button
              variant="contained"
              endIcon={saving ? <CircularProgress size={18} color="inherit" /> : <NextIcon />}
              disabled={saving}
              onClick={goNext}
              sx={{ background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)' }}
            >
              Continue
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default TenantOnboardWizard;
